import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import functions from "../../../../utils/Api";
import {
  dispatchRefreshApiCallUser,
  dispatchUserListData,
} from "../../../../Redux/dispatcher";
import ResuableTable from "../../../../components/ResuableTable/ResuableTable";
import DiscardChangesDialog from "../../../../components/DiscardChangesDialog/DiscardChangesDialog";
import Toaster from "../../../../components/Toaster/Toaster";
import { setToasterStatusMsg } from "../../../../Redux/reportSlice";
import { Box, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";

const UserList = ({
  userData,
  userListData,
  setUserData,
  activeTab,
  locationId,
  searchUser,
  tableHeaders,
  renderCell,
  loadingBtn,
  setLoadingBtn,
  selectedUser,
  userDetailsChanges,
  setUserDetailsChanges,
  page,
  setPage,
}) => {
  const dispatcher = useDispatch();
  const [userTypeActiveTab, setUserTypeActiveTab] = useState("1");
  const [employeeStatusTab, setEmployeeStatusTab] = useState("active"); // Tracks Active/Inactive

  const [permanentEmployees, setPermanentEmployees] = useState([]);
  const [contractEmployees, setContractEmployees] = useState([]);

  let userDetailsJson = useCallback(
    (data) => {
      const tableColumnData =
        data.length > 0
          ? data.map((item) => ({
              id: item.empId,
              name: `${item.firstName} ${item.lastName}`,
              email: item.email,
              role: item.designation,
              locationName: item.locationName || "_",
              status: item.isActive ? "Active" : "Inactive",
              locationNameId: item.officeLocationId,
              firstName: item.firstName,
              lastName: item.lastName,
              contactNumber: item.contactNumber,
              permission: item.permission
                ? item.permission.map((perm) => ({
                    permissionId: perm.permissionId,
                    permissionName: perm.permissionName,
                  }))
                : null,
              roleId: item.roleId || "",
            }))
          : [];
      setUserData(tableColumnData);
    },
    [setUserData]
  );

  const sortUser = useCallback(() => {
    let sortedData = [...userListData].sort(
      (a, b) => a.officeLocationId - b.officeLocationId
    );
    return sortedData;
  }, [userListData]);

  useEffect(() => {
    let filteredData = sortUser();
    if (locationId) {
      filteredData = filteredData.filter(
        (item) => Number(item.officeLocationId) === Number(locationId)
      );
    }
    if (searchUser) {
      filteredData = filteredData.filter((item) =>
        `${item.firstName} ${item.lastName}`
          .toLowerCase()
          .includes(searchUser.toLowerCase())
      );
    }
    userDetailsJson(filteredData);
  }, [locationId, searchUser, sortUser, userDetailsJson, userListData]);

  useEffect(() => {
    const permanentEmployeesData = userData
      ? userData.filter((item) => item.roleId === 1)
      : [];
    const contractEmployeeData = userData
      ? userData.filter((item) => item.roleId === 3)
      : [];
    setPermanentEmployees(permanentEmployeesData);
    setContractEmployees(contractEmployeeData);
  }, [userData]);

  const handleTabChange = (event, tabValue) => {
    setPage(0);
    setUserTypeActiveTab(tabValue);
  };

  const handleStatusTabChange = (event, tabValue) => {
    setPage(0);
    setEmployeeStatusTab(tabValue);
  };

  const filterByStatus = (employees, status) =>
    employees.filter((employee) => employee.status === status);

  const handleCreate = () => {
    let isActive = selectedUser.status === "Active" ? false : true;
    let updateUser = Object.entries(selectedUser) // Convert the object to an array of [key, value] pairs
      .filter(
        ([key, value]) =>
          key !== "name" &&
          value &&
          key !== "locationName" &&
          value &&
          key !== "status" &&
          value
      ) // Exclude "name" and falsy values
      .reduce((acc, [key, value]) => {
        acc[key] = value; // Reconstruct the object with the remaining key-value pairs
        return acc;
      }, {});
    updateUser["designation"] = updateUser.role;
    updateUser["isActive"] = isActive;
    delete updateUser.role;
    if (updateUser) {
      handeInActiveUser(updateUser);
    }
  };

  const handeInActiveUser = async (updatedUserData) => {
    setLoadingBtn(true);
    await functions
      .put(`/employee/${selectedUser.id}`, updatedUserData)
      .then((res) => {
        if (res) {
          dispatcher(dispatchRefreshApiCallUser(true));
          setLoadingBtn(false);
          setUserDetailsChanges(false);
          dispatcher(
            setToasterStatusMsg({
              msg: `User ${
                updatedUserData.isActive ? "Activated" : "InActivated"
              } Succesfully!`,
              severity: "success",
            })
          );
        }
      })
      .catch((err) => {
        setUserDetailsChanges(false);
        setLoadingBtn(false);
        dispatcher(
          setToasterStatusMsg({
            msg: `Error in ${
              updatedUserData.isActive ? "Activating " : "Activating"
            } The User!`,
            severity: "success",
          })
        );
      });
  };

  return (
    <Box sx={{ marginTop: "20px" }}>
      <TabContext value={userTypeActiveTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleTabChange} aria-label="User Type Tabs">
            <Tab
              label={`Permanent (${permanentEmployees.length})`}
              value="1"
              sx={{
                backgroundColor:
                  userTypeActiveTab === "1" ? "#004D72" : "transparent",
                color:
                  userTypeActiveTab === "1" ? "white !important" : "#004D72",
                border: userTypeActiveTab !== "1" && "1px solid #C5C9CF",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                borderBottom: "unset",
              }}
            />
            <Tab
              label={`Contract (${contractEmployees.length})`}
              value="2"
              sx={{
                backgroundColor:
                  userTypeActiveTab === "2" ? "#004D72" : "transparent",
                color:
                  userTypeActiveTab === "2" ? "white !important" : "#004D72",
                border: userTypeActiveTab !== "2" && "1px solid #C5C9CF",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                borderBottom: "unset",
              }}
            />
          </TabList>
        </Box>

        <TabPanel value="1" sx={{ padding: 0 }}>
          <TabContext value={employeeStatusTab}>
            <TabList onChange={handleStatusTabChange} aria-label="Status Tabs">
              <Tab
                label={`Active (${
                  filterByStatus(permanentEmployees, "Active").length
                })`}
                value="active"
              />
              <Tab
                label={`Inactive (${
                  filterByStatus(permanentEmployees, "Inactive").length
                })`}
                value="inactive"
              />
            </TabList>

            <TabPanel value="active" sx={{ padding: 0 }}>
              <ResuableTable
                userName="Employee"
                title="Manage Users"
                data={filterByStatus(permanentEmployees, "Active")}
                tableHeader={tableHeaders}
                renderCell={renderCell}
                page={page}
                setPage={setPage}
              />
            </TabPanel>

            <TabPanel value="inactive" sx={{ padding: 0 }}>
              <ResuableTable
                userName="Employee"
                title="Manage Users"
                data={filterByStatus(permanentEmployees, "Inactive")}
                tableHeader={tableHeaders}
                renderCell={renderCell}
                page={page}
                setPage={setPage}
              />
            </TabPanel>
          </TabContext>
        </TabPanel>

        <TabPanel value="2" sx={{ padding: 0 }}>
          <TabContext value={employeeStatusTab}>
            <TabList onChange={handleStatusTabChange} aria-label="Status Tabs">
              <Tab
                label={`Active (${
                  filterByStatus(contractEmployees, "Active").length
                })`}
                value="active"
              />
              <Tab
                label={`Inactive (${
                  filterByStatus(contractEmployees, "Inactive").length
                })`}
                value="inactive"
              />
            </TabList>

            <TabPanel value="active" sx={{ padding: 0 }}>
              <ResuableTable
                userName="Employee"
                title="Manage Users"
                data={filterByStatus(contractEmployees, "Active")}
                tableHeader={tableHeaders}
                renderCell={renderCell}
                page={page}
                setPage={setPage}
              />
            </TabPanel>

            <TabPanel value="inactive" sx={{ padding: 0 }}>
              <ResuableTable
                userName="Employee"
                title="Manage Users"
                data={filterByStatus(contractEmployees, "Inactive")}
                tableHeader={tableHeaders}
                renderCell={renderCell}
                page={page}
                setPage={setPage}
              />
            </TabPanel>
          </TabContext>
        </TabPanel>
      </TabContext>

      {userDetailsChanges && (
        <DiscardChangesDialog
          title={`${
            selectedUser.status === "Active" ? "Inactivate" : "Activate"
          } Employee`}
          text={`Are you sure you want to ${
            selectedUser.status === "Active" ? "Inactivate" : "Activate"
          } `}
          createBtn={`Yes ${
            selectedUser.status === "Active" ? "Inactivate" : "Activate"
          }`}
          user={selectedUser.name}
          open={userDetailsChanges}
          handleClose={setUserDetailsChanges}
          btnLoading={loadingBtn}
          setBtnLoading={setLoadingBtn}
          handleCreate={handleCreate}
        />
      )}
    </Box>
  );
};

export default UserList;
